import { Box, FormHelperText, InputBase, Tooltip, styled } from '@mui/material';
import Ajv from 'ajv';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import copyTextToClipboard from 'utils/clipboard.utils';
import { downloadFile } from 'utils/download.utils';
import { geojsonFeatureCollectionSchema, geojsonFeatureSchema } from './geojson.schema';

type IJsonEditorProps<ValueType = object> = {
  /** Value for controlled component */
  value?: ValueType;

  /** Default value for uncontrolled component */
  defaultValue?: ValueType;

  /** Placeholder JSON object */
  placeholder?: object;

  /** Custom error message, this error message displayed when value is not valid JSON */
  errorMessage?: string;

  /** Custom hint message */
  hintMessage?: string;

  /** Validation format */
  validationFormat?:
    | 'json'
    | 'geojson-feature-collection-point-polygon'
    | 'geojson-feature-point-polygon';

  /** Styles for the sizing
   * @default 300
   */
  minHeight?: number | string;

  /** Determines whether the value should be formatted on blur, `false` by default */
  formatOnBlur?: boolean;

  /** Styles for the sizing
   * @default 14
   */
  rows?: number;

  /**
   * File properties for the download button
   * If not specified, the file will be downloaded as a JSON file
   */
  fileProperties?: {
    name?: string;
    type?: string;
    extension?: string;
  };

  viewOnly?: boolean;

  /** Called when value changes
   * Depending on the input type object or string the value will serialize or deserialize
   * @default value: string
   */
  onChange?: (value: ValueType | null) => void;

  /** Function to serialize value into a string, used for value formatting, `JSON.stringify` by default */
  serialize?: typeof JSON.stringify;

  /** Function to deserialize string value, used for value formatting and input JSON validation, must throw error if string cannot be processed, `JSON.parse` by default */
  deserialize?: typeof JSON.parse;
};

const ajv = new Ajv();

const validateJson = (
  value: string,
  deserialize: typeof JSON.parse,
  format: 'json' | 'geojson-feature-collection-point-polygon' | 'geojson-feature-point-polygon'
) => {
  if (typeof value === 'string' && value.trim().length === 0) {
    return true;
  }

  try {
    const parsedValue = deserialize(value);

    if (format === 'geojson-feature-collection-point-polygon') {
      const validate = ajv.compile(geojsonFeatureCollectionSchema);
      const valid = validate(parsedValue);
      if (!valid) {
        return false;
      }
    } else if (format === 'geojson-feature-point-polygon') {
      const validate = ajv.compile(geojsonFeatureSchema);
      const valid = validate(parsedValue);
      if (!valid) {
        return false;
      }
    }

    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    return false;
  }
};

const formatValue = (value: string | object, serialize: typeof JSON.stringify): string => {
  try {
    if (typeof value === 'string' && value.trim().length === 0) {
      return value;
    } else {
      return serialize(value, null, 6);
    }
  } catch (e) {
    return 'Failure formatting value';
  }
};

const StyledContainer = styled(Box)<{
  'min-height': number | string;
  error: Tbooleanish;
  'is-focused': Tbooleanish;
}>(({ 'min-height': minHeight, error, 'is-focused': isFocused, theme }) => ({
  position: 'relative',
  background: '#fff',
  border: '1px solid',
  borderColor:
    error === 'true'
      ? theme.palette.error.main
      : isFocused === 'true'
      ? theme.palette.primary.main
      : theme.custom.colors.lightestBorder,
  padding: theme.spacing(1),
  borderRadius: theme.spacing(0.5),
  color: theme.custom.themeColors.success[100],
  minHeight,
  paddingBottom: theme.spacing(4),
}));

const HelperContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: theme.spacing(1),
  bottom: theme.spacing(2),
  /** Subtract the width of the copy button */
  maxWidth: 'calc(100% - 150px)',
  backgroundColor: '#fff',
}));

const ActionContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  gap: theme.spacing(1),
  flexDirection: 'column',
  right: theme.spacing(4),
  bottom: theme.spacing(2),
  color: theme.custom.colors.textLight,
}));

const StyledTextField = styled(InputBase)<{ 'min-height': number | string }>(
  ({ 'min-height': minHeight, theme }) => ({
    width: '100%',
    fontSize: 11,
    fontWeight: 400,

    '& textarea': {
      minHeight: `calc(${minHeight}px - 8px)`,
      height: 'auto !important',
      color: theme.custom.themeColors.success[100],
      overflow: 'auto !important',

      '&::placeholder': {
        color: theme.custom.themeColors.grayScale[100],
      },
    },
  })
);

const JsonEditor = <ValueType extends object>({
  value,
  defaultValue,
  errorMessage = 'Invalid JSON',
  validationFormat = 'json',
  hintMessage,
  placeholder,
  viewOnly,
  minHeight = 300,
  rows = 5,
  formatOnBlur = true,
  fileProperties = {
    name: 'data',
    type: 'application/json',
    extension: 'json',
  },
  onChange,
  serialize = JSON.stringify,
  deserialize = JSON.parse,
}: IJsonEditorProps<ValueType>) => {
  const [editorState, setEditorState] = useState<string>(() =>
    formatValue(defaultValue || value || '', serialize)
  );
  const [valid, setValid] = useState<boolean>(true);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const isControlled = !!value && onChange;

  useEffect(() => {
    if (isControlled) {
      formatValue(value, serialize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleCopy = () => {
    setTooltipOpen(true);
    copyTextToClipboard(editorState);
  };

  const handleDownload = async () => {
    const blob = new Blob([editorState], { type: fileProperties.type });
    const url = URL.createObjectURL(blob);
    await downloadFile(url, `${fileProperties.name}.${fileProperties.extension}`);
    URL.revokeObjectURL(url);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEditorState(e.currentTarget.value);
  };

  const handleChangeOnBlur = () => {
    const isValid = validateJson(editorState, deserialize, validationFormat);
    setValid(isValid);
    setIsFocused(false);
    if (formatOnBlur && isValid && editorState.trim() !== '') {
      setEditorState(formatValue(deserialize(editorState), serialize));
    }
    if (isValid) {
      onChange?.(editorState.trim() !== '' ? deserialize(editorState) : null);
    }
  };

  const handleOnFocus = () => {
    setValid(true);
    setIsFocused(true);
  };

  return (
    <StyledContainer
      min-height={minHeight}
      error={Booleanish(!valid)}
      is-focused={Booleanish(isFocused)}
    >
      <Box overflow="auto">
        <StyledTextField
          value={editorState}
          disabled={viewOnly}
          placeholder={placeholder ? serialize(placeholder, null, 6) : undefined}
          multiline
          min-height={minHeight}
          minRows={rows}
          onChange={handleChange}
          inputProps={{
            onBlur: handleChangeOnBlur,
            onFocus: handleOnFocus,
          }}
        />
      </Box>
      <HelperContainer>
        {!viewOnly && !valid && <FormHelperText error>{errorMessage}</FormHelperText>}
        {valid && hintMessage && editorState.trim().length === 0 && (
          <FormHelperText>
            <ThemeTypography variant="BODY_MEDIUM" color="GRAY_40">
              {hintMessage}
            </ThemeTypography>
          </FormHelperText>
        )}
      </HelperContainer>
      {!!editorState?.length && (
        <ActionContainer>
          {valid && (
            <ThemeButton
              color="BLUE_ICE"
              startIcon={<Icon name="download" />}
              onClick={handleDownload}
            >
              Download
            </ThemeButton>
          )}
          <Tooltip
            title="Content copied to clipboard!"
            onClose={() => setTooltipOpen(false)}
            open={tooltipOpen}
            leaveDelay={800}
          >
            <ThemeButton color="BLUE_ICE" startIcon={<Icon name="copy" />} onClick={handleCopy}>
              Copy data
            </ThemeButton>
          </Tooltip>
        </ActionContainer>
      )}
    </StyledContainer>
  );
};

export default JsonEditor;
